import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import { getFileList } from '../../actions/dashboard';
import Loader from '../../components/Loader';
import Table from '../../components/Table';
import FileUpload from '../../components/FileUpload';
import TableSearch from '../../components/TableSearch';
import { api } from '../../App';
import styles from './styles.module.scss';

const Fileexchange = () => {
  const dispatch = useDispatch();
  const { file_list, loading } = useSelector(state => state.dashboard);
  const [filteredData, setFilteredData] = useState(null);
  const [sortState, setSortState] = useState({ key: null, order: null });
  const [openSearchKey, setOpenSearchKey] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    dispatch(getFileList());
  }, [dispatch]);

  const handleFilter = useCallback((result, query) => {
    if (!query) {
      setFilteredData(null);
      return;
    }
    setFilteredData(result || []);
  }, []);

  const handleSort = useCallback((sortedData, sortKey, sortOrder) => {
    setSortState({ key: sortOrder ? sortKey : null, order: sortOrder });
  }, []);

  const handleSearchOpen = useCallback((searchKey) => {
    setOpenSearchKey(searchKey);
  }, []);

  // Комбинируем фильтрацию и сортировку
  const dataToRender = React.useMemo(() => {
    let data = filteredData ?? file_list;

    // Фильтрация по дате
    if (dateFrom || dateTo) {
      data = data.filter((item) => {
        if (!item.loadTime) return false;

        const itemDate = moment(item.loadTime).startOf('day');
        const fromDate = dateFrom ? moment(dateFrom).startOf('day') : null;
        const toDate = dateTo ? moment(dateTo).endOf('day') : null;

        if (fromDate && itemDate.isBefore(fromDate)) return false;
        if (toDate && itemDate.isAfter(toDate)) return false;

        return true;
      });
    }

    // Сортировка первичного массива по алфавиту (если нет активной сортировки)
    if (!sortState.order || !sortState.key) {
      if (data && data.length > 0) {
        data = [...data].sort((a, b) => {
          const aName = String(a?.OrgName || '').toLowerCase().trim();
          const bName = String(b?.OrgName || '').toLowerCase().trim();
          return aName.localeCompare(bName, 'ru', { numeric: true });
        });
      }
    }

    if (sortState.order && sortState.key) {
      const sorted = data && [...data]?.sort((a, b) => {
        const aValue = a?.[sortState.key];
        const bValue = b?.[sortState.key];

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortState.order === 'asc' ? aValue - bValue : bValue - aValue;
        }

        const aStr = String(aValue).toLowerCase().trim();
        const bStr = String(bValue).toLowerCase().trim();

        if (sortState.order === 'asc') {
          return aStr.localeCompare(bStr, 'ru', { numeric: true });
        } else {
          return bStr.localeCompare(aStr, 'ru', { numeric: true });
        }
      });
      return sorted;
    }

    return data;
  }, [filteredData, file_list, sortState, dateFrom, dateTo]);

  if (loading) return <Loader />;

  const handleUpload = async (files) => {
    const formData = new FormData();

    files.forEach(file => {
      // имя поля зависит от backend API; при необходимости скорректируйте
      formData.append('files', file);
    });

    await api.post('/cons_file/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    // после успешной загрузки обновляем список файлов
    dispatch(getFileList());
  };

  const renderRow = (item) => {
    const time = item.loadTime !== null && moment(item.loadTime).format('DD.MM.YYYY');

    return (
      <tr key={item.id}>
        <td>{item.OrgName}</td>
        <td>{item.filename}</td>
        <td style={{ textAlign: 'center', width: '130px' }}>{time}</td>
      </tr>
    );
  };

  return (
    <section>
      <div className={styles.dateFilter}>
        <div className={styles.dateFilterRow}>
          <label className={styles.dateLabel}>
            <span>С:</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className={styles.dateInput}
            />
          </label>
          <label className={styles.dateLabel}>
            <span>По:</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className={styles.dateInput}
              min={dateFrom || undefined}
            />
          </label>
          {(dateFrom || dateTo) && (
            <button
              type="button"
              onClick={() => {
                setDateFrom('');
                setDateTo('');
              }}
              className={styles.clearDateButton}
            >
              Сбросить
            </button>
          )}
        </div>
      </div>
      <Table maxHeight="86vh" minHeight="500px" dataLoaded={!loading && file_list?.length > 0}>
        <thead>
          <tr>
            <th>
              <div className={styles.headerCellWrapper}>
                <div>Отправитель</div>
                <div>
                  <TableSearch
                    data={file_list}
                    searchKey="OrgName"
                    onFilter={handleFilter}
                    onSort={handleSort}
                    activeSortKey={sortState.key}
                    activeSortOrder={sortState.order}
                    onSearchOpen={handleSearchOpen}
                    openSearchKey={openSearchKey}
                  />
                </div>
              </div>
            </th>
            <th>
              <div className={styles.headerCellWrapper}>
                <div>Файл</div>
                <div>
                  <TableSearch
                    data={file_list}
                    searchKey="filename"
                    onFilter={handleFilter}
                    onSort={handleSort}
                    activeSortKey={sortState.key}
                    activeSortOrder={sortState.order}
                    onSearchOpen={handleSearchOpen}
                    openSearchKey={openSearchKey}
                  />
                </div>
              </div>
            </th>
            <th>
              <div className={styles.headerCellWrapper}>
                <div>Дата</div>
                <div>
                  <TableSearch
                    data={file_list}
                    searchKey="loadTime"
                    onFilter={handleFilter}
                    onSort={handleSort}
                    activeSortKey={sortState.key}
                    activeSortOrder={sortState.order}
                    onSearchOpen={handleSearchOpen}
                    openSearchKey={openSearchKey}
                  />
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {dataToRender?.map(renderRow)}
        </tbody>
      </Table>
    </section>
  );
};

export default Fileexchange;